import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import React, {ReactNode, useEffect, useState} from 'react';
import isEqual from 'react-fast-compare';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {NON_STANDARD_AMINO_ACIDS} from 'biojs/Codons';
import {containsNonStandardAminoAcids} from 'biojs/Util';
import DataConnector from 'connect/DataConnector';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import staticURL from 'lib/staticURL';
import MolstarViewer, {MOLSTAR_PREVIEW_TYPE_TO_FORMAT} from 'molbio/molstar/ViewerWrapper';
import ProteinStore from 'molbio/proteins/ProteinStore';
import {ProteinStructureExternalFile, ProteinVersion} from 'molbio/proteins/util/ProteinConstants';
import {
  StructurePredictionModel,
  getModelDisplayName,
} from 'molbio/proteins/util/StructurePredictionConstants';
import notifications from 'notifications';
import {FONT_PROXIMA_13, FONT_PROXIMA_17} from 'styles/FontConstants';
import {proteinSelectionUIGlobalState} from 'toolbar/protein/ProteinUIGlobalState';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import usePrevious from 'ui-library/hooks/usePrevious';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import runModal from 'ui-library/runModal';
import Spinner from 'ui-library/Spinner';

import {
  HasInProgressAlphaFoldPredictionQuery,
  HasInProgressAlphaFoldPredictionQueryVariables,
} from './__generated/HasInProgressAlphaFoldPredictionQuery';
import {RemainingExecutionsAndTenantNameQuery} from './__generated/RemainingExecutionsAndTenantNameQuery';
import {UserVerificationStatusQuery} from './__generated/UserVerificationStatusQuery';
import {getStructureInfoFromVersions, requestRun} from './AlphaFoldTabUtils';
import ModelSelector from './ModelSelector';
import RedoPredictionModal from './RedoPredictionModal';

const MAXIMUM_SEQUENCE_LENGTH = 1500;
const INSUFFICIENT_PERMISSIONS_MESSAGE =
  'You do not have sufficient permissions to request a structure for this AA sequence.';
const EXCEEDS_MAX_LENGTH_MESSAGE = `At the moment, you cannot request structures for sequences with over ${MAXIMUM_SEQUENCE_LENGTH} residues.`;
const NON_STANDARD_RESIDUES_MESSAGE = `This protein contains at least one non-standard amino acid (${NON_STANDARD_AMINO_ACIDS.join(
  ', '
)}) which cannot produce a valid AlphaFold structure prediction.`;
const NO_RESIDUES_MESSAGE =
  'You cannot run AlphaFold on this AA sequence since it does not contain any residues.';

function AlphaFoldPaperLink(): JSX.Element {
  return (
    <a href="https://www.nature.com/articles/s41586-021-03819-2" target="_blank" rel="noopener noreferrer">
      AlphaFold 2
    </a>
  );
}

function AlphaFoldCitation({className}: {className?: string}): JSX.Element {
  return (
    <p className={className}>
      3D structures are predicted using&nbsp;
      <AlphaFoldPaperLink />.
    </p>
  );
}

export function getRunBlockingMessage(
  aminoAcids: string,
  permissions: Permissions,
  remainingRuns: number
): string | null {
  if (remainingRuns <= 0) {
    return 'You do not have any structure prediction runs available currently.';
  } else if (!permissions.polymerFeaturesEditable) {
    return INSUFFICIENT_PERMISSIONS_MESSAGE;
  } else if (containsNonStandardAminoAcids(aminoAcids)) {
    return NON_STANDARD_RESIDUES_MESSAGE;
  } else if (aminoAcids.length > MAXIMUM_SEQUENCE_LENGTH) {
    return EXCEEDS_MAX_LENGTH_MESSAGE;
  } else if (aminoAcids.length === 0) {
    return NO_RESIDUES_MESSAGE;
  } else {
    return null;
  }
}

export const USER_VERIFICATION_STATUS: DocumentNode = gql`
  query UserVerificationStatusQuery @skipAudit @engComponent(name: MOLBIO) {
    currentUser {
      ... on UserNode {
        id
        institution {
          id
          verified
        }
        verified
      }
    }
  }
`;

export const REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY: DocumentNode = gql`
  query RemainingExecutionsAndTenantNameQuery @skipAudit @engComponent(name: MOLBIO) {
    remainingAlphafoldExecutions
    currentTenant {
      ... on TenantNode {
        id
        displayName
      }
    }
  }
`;

function ViewerPane({
  structureFile,
  structureFileOutOfDate,
  predictionInProgress,
  proteinId,
  structureProteinVersionId,
  aminoAcids,
  permissions,
  selectionStart,
  selectionEnd,
  renderWarningMessage,
}: {
  structureFile: ProteinStructureExternalFile | null;
  structureFileOutOfDate: boolean;
  predictionInProgress: boolean;
  proteinId: string;
  structureProteinVersionId: number;
  aminoAcids: string;
  permissions: Permissions;
  selectionStart: number;
  selectionEnd: number | null;
  renderWarningMessage: (message: ReactNode) => JSX.Element;
}): JSX.Element {
  const previousStructureFile = usePrevious(structureFile);
  const [signedStructureURL, setSignedStructureURL] = useState<string | null>(null);
  const userVerificationStatusQueryResult =
    useBenchlingQuery<UserVerificationStatusQuery>(USER_VERIFICATION_STATUS);
  useEffect(() => {
    // Check that value has actually changed and not just the object reference, otherwise this causes
    // an unnecessary re-render of the viewer because the signed URL is always unique.

    if (structureFile && !isEqual(structureFile, previousStructureFile)) {
      const fetchSignedUrl = async (): Promise<void> => {
        const signedURL = await performSignedFileQuery(
          structureFile.id,
          structureFile.url,
          proteinId,
          structureProteinVersionId
        );
        if (signedURL == null) {
          notifications.error('Error fetching structure file. Please refresh the page.');
        } else {
          setSignedStructureURL(signedURL);
        }
      };
      fetchSignedUrl();
    }
  }, [structureFile, previousStructureFile, proteinId, structureProteinVersionId]);

  if (userVerificationStatusQueryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (userVerificationStatusQueryResult.type === QueryResultType.ERROR) {
    notifications.error('Error when loading AlphaFold details. Please refresh the page.');
    return <></>;
  }

  if (structureFile == null) {
    return (
      <div className={css(styles.smallTabSpacing)}>
        <p className={css(styles.largestText)}>Predicting 3D structure...</p>
        <p className={css(styles.smallerText)}>
          This could take some time. You will receive an email when it’s ready.
        </p>
        <AlphaFoldCitation className={css(styles.smallerText)} />
      </div>
    );
  } else {
    const currentUser = userVerificationStatusQueryResult.data.currentUser;
    assert(
      currentUser?.__typename === 'UserNode' || currentUser == null,
      `Non-user should not access this page.`
    );

    let warningMessage: ReactNode | null = null;
    if (structureFileOutOfDate) {
      warningMessage = 'This structure is out of sync.';
      if (predictionInProgress) {
        // NOTE: this message doesn't handle the case where a different user requested the run.
        warningMessage = `${warningMessage} You’ll receive an email when your updated structure is ready.`;
      } else if (getRunBlockingMessage(aminoAcids, permissions, 1)) {
        warningMessage = `${warningMessage} ${getRunBlockingMessage(aminoAcids, permissions, 1)}`;
      } else if (
        BenchlingConfig.MOLBIO_ALPHAFOLD_IS_ACADEMIC &&
        (!currentUser?.institution?.verified || !currentUser?.verified)
      ) {
        warningMessage = `${warningMessage} Structure prediction is only available to verified academic users from verified institutions.`;
      } else {
        warningMessage = (
          <span>
            {warningMessage}{' '}
            <Button
              buttonStyle={ButtonStyle.LINK}
              className={css(styles.buttonLinkText)}
              text="Redo structure prediction"
              onClick={() => {
                trackEvent(MOLBIO_ANALYTICS_ACTIONS.CLICKED_ALPHAFOLD_REDO_RUN);
                runModal(RedoPredictionModal, {proteinId});
              }}
            />
            .
          </span>
        );
      }
    }

    if (signedStructureURL == null) {
      return <CenteredHorizontalThrobber />;
    }
    return (
      <div className={css(styles.viewerWrapper)}>
        {warningMessage && renderWarningMessage(warningMessage)}
        <MolstarViewer
          url={signedStructureURL}
          format={MOLSTAR_PREVIEW_TYPE_TO_FORMAT[structureFile.previewType]}
          className={classNames('reset', css(styles.viewer))}
          interactionAnalyticsCallback={() => {
            trackEvent(MOLBIO_ANALYTICS_ACTIONS.INTERACTED_ALPHAFOLD_VIEWER);
          }}
          residueStart={structureFileOutOfDate ? undefined : selectionStart}
          residueEnd={structureFileOutOfDate ? undefined : selectionEnd}
        />
      </div>
    );
  }
}

export const HAS_IN_PROGRESS_ALPHAFOLD_PREDICTION: DocumentNode = gql`
  query HasInProgressAlphaFoldPredictionQuery($proteinId: StrictString!) @skipAudit {
    protein(id: $proteinId) {
      ... on ProteinNode {
        id
        alphafoldPredictionInProgress
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface AlphaFoldTabContentProps {
  proteinId: string;
}

interface AlphaFoldTabContentEnhancedProps {
  versions?: Array<ProteinVersion>;
  aminoAcids: string;
  permissions: Permissions;
}

class AlphaFoldTabContentConnector extends DataConnector<
  AlphaFoldTabContentProps,
  AlphaFoldTabContentEnhancedProps
> {
  shouldSubscribeToRedux = false;
  stores = [ProteinStore];

  enhanceProps(): AlphaFoldTabContentEnhancedProps {
    const proteinStore = this.proteinStore();
    const protein = proteinStore.getProteinById(this.props.proteinId)!;
    return {
      versions: proteinStore.getVersionsById(this.props.proteinId),
      aminoAcids: protein.version.aminoAcids,
      permissions: protein.permissions,
    };
  }
}

function AlphaFoldLandingPage({
  aminoAcids,
  permissions,
  predictionInProgress,
  proteinId,
}: {
  aminoAcids: string;
  permissions: Permissions;
  predictionInProgress: boolean;
  proteinId: string;
}): JSX.Element {
  const remainingExecutionsAndTenantNameQueryResult =
    useBenchlingQuery<RemainingExecutionsAndTenantNameQuery>(REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY);
  const userVerificationStatusQueryResult =
    useBenchlingQuery<UserVerificationStatusQuery>(USER_VERIFICATION_STATUS);

  const [selectedModel, setSelectedModel] = useState<StructurePredictionModel>(
    StructurePredictionModel.ALPHAFOLD
  );
  const [awaitingResponse, setAwaitingResponse] = useState<boolean>(false);

  const showModelSelector = BenchlingConfig.MOLBIO_ENABLE_STRUCTURE_PREDICTION_MODELS;

  if (
    remainingExecutionsAndTenantNameQueryResult.type === QueryResultType.PENDING ||
    userVerificationStatusQueryResult.type === QueryResultType.PENDING
  ) {
    return <CenteredHorizontalThrobber />;
  } else if (
    remainingExecutionsAndTenantNameQueryResult.type === QueryResultType.ERROR ||
    userVerificationStatusQueryResult.type === QueryResultType.ERROR
  ) {
    notifications.error('Error when loading AlphaFold details. Please refresh the page.');
    return <></>;
  }

  let header: string = 'Predict 3D structure';
  let subheader: ReactNode = (
    <>
      Predict structures of proteins up to {MAXIMUM_SEQUENCE_LENGTH} residues using{' '}
      {showModelSelector ? getModelDisplayName(selectedModel) : <AlphaFoldPaperLink />}
    </>
  );
  if (predictionInProgress) {
    header = 'Predicting 3D structure...';
    subheader = 'This could take some time. You will receive an email when the structure is ready.';
  }
  const currentUser = userVerificationStatusQueryResult.data.currentUser;
  assert(
    currentUser?.__typename === 'UserNode' || currentUser == null,
    `Non-user should not access this page.`
  );

  let blockingBannerMessage: string | null = null;
  if (
    BenchlingConfig.MOLBIO_ALPHAFOLD_IS_ACADEMIC &&
    (!currentUser?.institution?.verified || !currentUser?.verified)
  ) {
    blockingBannerMessage =
      'Structure prediction is only available to verified academic users from verified institutions.';
  } else {
    blockingBannerMessage = getRunBlockingMessage(
      aminoAcids,
      permissions,
      remainingExecutionsAndTenantNameQueryResult.data.remainingAlphafoldExecutions
    );
  }

  return (
    <>
      {blockingBannerMessage && (
        <div className={css(styles.largeInlineBannerMargin)}>
          <InlineBanner text={blockingBannerMessage} bannerType={InlineBannerType.WARNING} />
        </div>
      )}
      <div className={css(styles.centered, styles.imgMargin)}>
        <img
          width={255}
          height={175}
          src={staticURL('/static/img/alphafold_example_quality_structure.png')}
        />
      </div>
      <p className={css(styles.largestText, styles.centered)}>{header}</p>
      <p className={css(styles.smallerText, styles.grayDarker, styles.centered)}>{subheader}</p>

      {!predictionInProgress && showModelSelector && (
        <div className={css(styles.centered, styles.modelSelectorContainer)}>
          <ModelSelector
            selectedModel={selectedModel}
            onModelChange={setSelectedModel}
            disabled={blockingBannerMessage != null}
            className={css(styles.modelSelector)}
          />
        </div>
      )}

      {!predictionInProgress && (
        <div className={css(styles.centered)}>
          <Button
            disabled={blockingBannerMessage != null || awaitingResponse}
            onClick={async () => {
              setAwaitingResponse(true);
              try {
                await requestRun(proteinId, remainingExecutionsAndTenantNameQueryResult.data, selectedModel);
                trackEvent(MOLBIO_ANALYTICS_ACTIONS.REQUESTED_ALPHAFOLD_RUN);
              } finally {
                setAwaitingResponse(false);
              }
            }}
            buttonStyle={ButtonStyle.SECONDARY}
            text="Predict 3D structure"
            size={ButtonSize.MEDIUM}
            className={css(styles.predictButton)}
          />
        </div>
      )}

      {awaitingResponse && (
        <div className={css(styles.centered)}>
          <Spinner />
        </div>
      )}
    </>
  );
}

function AlphaFoldTabContent({
  proteinId,
  versions,
  aminoAcids,
  permissions,
}: AlphaFoldTabContentProps & AlphaFoldTabContentEnhancedProps): JSX.Element {
  const [proteinSelectionUIState] = proteinSelectionUIGlobalState.useGlobalClientState(proteinId);
  const {start: selectionStart, end: selectionEnd} = proteinSelectionUIState;
  const structureInfo = getStructureInfoFromVersions(versions);
  const queryResult = useBenchlingQuery<
    HasInProgressAlphaFoldPredictionQuery,
    HasInProgressAlphaFoldPredictionQueryVariables
  >(HAS_IN_PROGRESS_ALPHAFOLD_PREDICTION, {
    variables: {proteinId},
  });
  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }
  if (queryResult.type === QueryResultType.ERROR) {
    notifications.error('Error loading AlphaFold details. Please refresh the page.');
    return <></>;
  }
  const hasPredictionInProgress =
    queryResult.type === QueryResultType.SUCCESS &&
    !isInaccessible(queryResult.data.protein) &&
    queryResult.data.protein.alphafoldPredictionInProgress;
  if (structureInfo.structureFile != null) {
    return (
      <ViewerPane
        structureFile={structureInfo.structureFile}
        structureFileOutOfDate={Boolean(structureInfo.structureFile && structureInfo.structureFileOutOfDate)}
        predictionInProgress={hasPredictionInProgress}
        proteinId={proteinId}
        structureProteinVersionId={structureInfo.versionId}
        aminoAcids={aminoAcids}
        permissions={permissions}
        selectionStart={selectionStart}
        selectionEnd={selectionEnd}
        renderWarningMessage={(message) => (
          <div className={css(styles.smallInlineBannerMargin)}>
            <InlineBanner text={message} bannerType={InlineBannerType.WARNING} />
          </div>
        )}
      />
    );
  } else {
    return (
      <AlphaFoldLandingPage
        aminoAcids={aminoAcids}
        permissions={permissions}
        predictionInProgress={hasPredictionInProgress}
        proteinId={proteinId}
      />
    );
  }
}

const styles = StyleSheet.create({
  grayDarker: {
    color: GRAY_DARKER,
  },
  smallTabSpacing: {
    margin: '16px',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
  },
  largestText: FONT_PROXIMA_17,
  smallerText: FONT_PROXIMA_13,
  imgMargin: {
    margin: '34px 0px',
  },
  modelSelectorContainer: {
    margin: '20px 0px',
  },
  modelSelector: {
    minWidth: '300px',
    textAlign: 'left',
  },
  predictButton: {
    margin: '30px 0px 12px',
  },
  buttonLinkText: {
    verticalAlign: 'baseline',
  },
  viewerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  viewer: {
    borderWidth: '0px',
    flex: '1 1 auto',
  },
  largeInlineBannerMargin: {
    margin: '18px 11px',
  },
  smallInlineBannerMargin: {
    margin: '4px',
  },
});

export default AlphaFoldTabContentConnector.connect(AlphaFoldTabContent);
